import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Loader2, CreditCard, CheckCircle, XCircle, Settings } from 'lucide-react';
import { Logo } from '@/components/Logo';
import { authController } from '@/states/authStateController';
import { useAuth } from '@chainlit/react-client';
import {
  BASE_URL,
  SUBSCRIPTION_STATUS_ENDPOINT,
  VERIFY_SESSION_ENDPOINT,
  CANCEL_SUBSCRIPTION_ENDPOINT,
  PAYMENT_HISTORY_ENDPOINT,
} from '@/lib/constants';

interface SubscriptionData {
  is_subscribed: boolean;
  subscription_id: string | null;
  subscription_expiry_date: string | null;
}

interface InvoiceItem {
  id: string;
  amount: number;
  currency: string;
  status: string;
  date: string;
  invoice_url: string | null;
}

export default function SubscriptionDashboard() {
  // Starts true (rather than false) so the very first render — before the
  // mount effect below has had a chance to run — shows the loading spinner
  // instead of a "No Active Subscription" flash while status is unknown.
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [subscriptionData, setSubscriptionData] = useState<SubscriptionData | null>(null);
  const [paymentHistory, setPaymentHistory] = useState<InvoiceItem[]>([]);
  const [isCancelling, setIsCancelling] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { logout: chainlitLogout } = useAuth();

  const { user, isLoading: userLoading } = authController.useState(['user', 'isLoading']);

  // Fetch subscription status
  const fetchSubscriptionStatus = async () => {
    if (!user?.id) return;

    try {
      setIsLoading(true);
      const response = await fetch(
        `${BASE_URL}${SUBSCRIPTION_STATUS_ENDPOINT}?user_id=${user.id}`,
        {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setSubscriptionData({
          is_subscribed: data.is_subscribed,
          subscription_id: data.subscription_id,
          subscription_expiry_date: data.subscription_expiry_date,
        });
        // Update auth controller
        authController.updateSubscriptionStatus({
          is_subscribed: data.is_subscribed,
          subscription_id: data.subscription_id,
          subscription_expiry_date: data.subscription_expiry_date
            ? new Date(data.subscription_expiry_date).toISOString()
            : null,
        });
      }
    } catch (error) {
      console.error('Error fetching subscription status:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Independently confirm a just-completed Stripe checkout directly with our
  // API (which in turn checks Stripe), instead of relying solely on the
  // webhook having already landed. The webhook and this call race each other
  // after a real payment; a few quick retries cover the case where the
  // backend momentarily hasn't processed things yet. Returns true if the
  // subscription was confirmed active.
  const verifyCheckoutSession = async (sessionId: string): Promise<boolean> => {
    if (!user?.id) return false;

    const maxAttempts = 4;
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        const response = await fetch(
          `${BASE_URL}${VERIFY_SESSION_ENDPOINT}?session_id=${sessionId}&user_id=${user.id}`,
          {
            method: 'GET',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          if (data.is_subscribed) {
            setSubscriptionData({
              is_subscribed: data.is_subscribed,
              subscription_id: data.subscription_id,
              subscription_expiry_date: data.subscription_expiry_date,
            });
            authController.updateSubscriptionStatus({
              is_subscribed: data.is_subscribed,
              subscription_id: data.subscription_id,
              subscription_expiry_date: data.subscription_expiry_date
                ? new Date(data.subscription_expiry_date).toISOString()
                : null,
            });
            return true;
          }
        }
      } catch (error) {
        console.error('Error verifying checkout session:', error);
      }

      if (attempt < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, 750 * attempt));
      }
    }
    return false;
  };

  // Fetch subscription status on mount
  useEffect(() => {
    const fetchPaymentHistory = async () => {
      if (!user?.id) return;

      try {
        const response = await fetch(
          `${BASE_URL}${PAYMENT_HISTORY_ENDPOINT}?user_id=${user.id}`,
          {
            method: 'GET',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          setPaymentHistory(data.invoices || []);
        }
      } catch (error) {
        console.error('Error fetching payment history:', error);
      }
    };

    const run = async () => {
      if (!user?.id) return;

      // Landed here straight from Stripe checkout (success_url includes
      // session_id). Confirm it directly rather than waiting on the webhook
      // — see verifyCheckoutSession above for why. Keep the loading state on
      // the whole time so the user only ever sees "loading" then
      // "subscribed", never a premature "not subscribed".
      const sessionId = searchParams.get('session_id');
      let verified = false;
      if (sessionId) {
        setIsLoading(true);
        verified = await verifyCheckoutSession(sessionId);
        // Drop session_id from the URL so refreshing doesn't re-verify.
        setSearchParams({}, { replace: true });
      }

      if (!verified) {
        // Owns setIsLoading(true)/(false) itself, including the "verify
        // failed" case above, so loading always ends up cleared.
        await fetchSubscriptionStatus();
      } else {
        setIsLoading(false);
      }
    };

    run();
    // Fetch payment history if user has an active subscription or subscription hasn't expired
    const hasActiveAccess =
      user?.is_subscribed ||
      (user?.subscription_expiry_date &&
        new Date(user.subscription_expiry_date) > new Date());
    if (hasActiveAccess) {
      fetchPaymentHistory();
    }
  }, [user?.id, user?.is_subscribed, user?.subscription_expiry_date]);

  const handleCancelSubscription = async () => {
    if (!user?.id) {
      setError('Unable to cancel subscription. Please contact support.');
      return;
    }

    try {
      setIsCancelling(true);
      setError('');
      setShowCancelDialog(false);

      const response = await fetch(
        `${BASE_URL}${CANCEL_SUBSCRIPTION_ENDPOINT}?user_id=${user.id}`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        // Refresh subscription status (this will update both subscriptionData and user state)
        await fetchSubscriptionStatus();
        // Also fetch payment history if expiry date is set (access still valid)
        if (data.cancelled_at && new Date(data.cancelled_at) > new Date()) {
          const response = await fetch(
            `${BASE_URL}${PAYMENT_HISTORY_ENDPOINT}?user_id=${user.id}`,
            {
              method: 'GET',
              credentials: 'include',
              headers: {
                'Content-Type': 'application/json',
              },
            }
          );
          if (response.ok) {
            const historyData = await response.json();
            setPaymentHistory(historyData.invoices || []);
          }
        }
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to cancel subscription');
      }
    } catch (error) {
      setError('Network error occurred');
    } finally {
      setIsCancelling(false);
    }
  };

  const handleLogout = () => {
    chainlitLogout(true);
  };

  // Show loading state while checking authentication
  if (userLoading || isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-200 border-t-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect if no user is logged in
  if (!user) {
    navigate('/login');
    return null;
  }

  const expiryDate = subscriptionData?.subscription_expiry_date
    ? new Date(subscriptionData.subscription_expiry_date)
    : null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex flex-col">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-md border-b border-slate-200/50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <Logo className="h-8" />
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => navigate('/')}
                className="border-slate-300 bg-white/50 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 hover:border-blue-300 hover:shadow-md transition-all duration-200 text-slate-700 hover:text-blue-700 font-medium"
              >
                Go to App
              </Button>
              <Button
                variant="outline"
                onClick={handleLogout}
                className="border-slate-300 bg-white/50 hover:bg-gradient-to-r hover:from-slate-50 hover:to-slate-100 hover:border-slate-400 hover:shadow-md transition-all duration-200 text-slate-700 hover:text-slate-900 font-medium"
              >
                Logout
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto space-y-8">
          <div className="text-center">
            <h1 className="text-3xl font-extrabold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              Subscription Dashboard
            </h1>
            <p className="mt-2 text-sm text-slate-600">
              Manage your subscription and billing
            </p>
          </div>

          {error && (
            <Alert variant="destructive" className="border-red-200 bg-red-50">
              <XCircle className="h-4 w-4 text-red-600" />
              <AlertDescription className="text-red-800">{error}</AlertDescription>
            </Alert>
          )}

          <Card className="border-2 border-slate-200 shadow-xl bg-white/90 backdrop-blur-sm">
            <CardHeader className="border-b border-slate-100 bg-gradient-to-r from-blue-50 to-indigo-50">
              <CardTitle className="text-xl text-slate-900">
                Subscription Status
              </CardTitle>
              <CardDescription className="text-slate-600">
                Your current subscription information
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6 pt-6">
              {subscriptionData?.is_subscribed ||
              (subscriptionData?.subscription_expiry_date &&
                new Date(subscriptionData.subscription_expiry_date) > new Date()) ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-4 rounded-lg border-2 bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
                    {subscriptionData?.is_subscribed ? (
                      <>
                        <div className="relative">
                          <div className="absolute inset-0 bg-green-400 rounded-full blur-lg opacity-30"></div>
                          <CheckCircle
                            className="relative h-6 w-6 text-green-600"
                            strokeWidth={2.5}
                          />
                        </div>
                        <span className="text-lg font-semibold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                          Active Subscription
                        </span>
                      </>
                    ) : (
                      <>
                        <div className="relative">
                          <div className="absolute inset-0 bg-amber-400 rounded-full blur-lg opacity-30"></div>
                          <XCircle
                            className="relative h-6 w-6 text-amber-600"
                            strokeWidth={2.5}
                          />
                        </div>
                        <span className="text-lg font-semibold bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">
                          Subscription Cancelled - Active Until Expiry
                        </span>
                      </>
                    )}
                  </div>

                  {expiryDate && (
                    <div className="space-y-3 p-4 bg-slate-50 rounded-lg border border-slate-200">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-slate-600">
                          {subscriptionData?.is_subscribed
                            ? 'Next billing date:'
                            : 'Access expires on:'}
                        </span>
                        <span className="text-sm font-semibold text-slate-900">
                          {expiryDate.toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                          })}
                        </span>
                      </div>
                      <div className="flex justify-between items-center pt-2 border-t border-slate-200">
                        <span className="text-sm font-medium text-slate-600">
                          Subscription ID:
                        </span>
                        <span className="text-xs font-mono text-slate-700 bg-slate-100 px-2 py-1 rounded">
                          {subscriptionData.subscription_id?.substring(0, 20)}...
                        </span>
                      </div>
                    </div>
                  )}

                  {subscriptionData?.is_subscribed ? (
                    <>
                      <Button
                        onClick={() => setShowCancelDialog(true)}
                        disabled={isCancelling}
                        className="w-full bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700 text-white shadow-lg hover:shadow-xl transition-all duration-200"
                        variant="destructive"
                      >
                        <XCircle className="mr-2 h-4 w-4" />
                        Cancel Subscription
                      </Button>
                      <AlertDialog
                        open={showCancelDialog}
                        onOpenChange={setShowCancelDialog}
                      >
                        <AlertDialogContent className="border-2 border-slate-200">
                          <AlertDialogHeader>
                            <AlertDialogTitle className="text-slate-900">
                              Cancel Subscription?
                            </AlertDialogTitle>
                            <AlertDialogDescription className="text-slate-600">
                              Are you sure you want to cancel your subscription? You will
                              have access until the end of your current billing period.
                              After that, you'll need to resubscribe to continue using the
                              service.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel
                              disabled={isCancelling}
                              className="hover:bg-slate-100"
                            >
                              Keep Subscription
                            </AlertDialogCancel>
                            <AlertDialogAction
                              onClick={handleCancelSubscription}
                              disabled={isCancelling}
                              className="bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700 text-white"
                            >
                              {isCancelling ? (
                                <>
                                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                  Cancelling...
                                </>
                              ) : (
                                'Cancel Subscription'
                              )}
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </>
                  ) : (
                    (() => {
                      // Check if subscription is cancelled but not yet expired
                      const isNotSubscribedButNotExpired = Boolean(
                        !subscriptionData?.is_subscribed &&
                          subscriptionData?.subscription_expiry_date &&
                          new Date(subscriptionData.subscription_expiry_date) > new Date()
                      );

                      const resubscribeButton = (
                        <Button
                          onClick={() => navigate('/payment')}
                          disabled={isNotSubscribedButNotExpired}
                          className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-lg hover:shadow-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-lg"
                          size="lg"
                        >
                          <CreditCard className="mr-2 h-4 w-4" />
                          Resubscribe
                        </Button>
                      );

                      if (isNotSubscribedButNotExpired) {
                        return (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="w-full">{resubscribeButton}</div>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>You can resubscribe when your subscription expires</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        );
                      }

                      return resubscribeButton;
                    })()
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-4 rounded-lg border-2 bg-gradient-to-r from-red-50 to-rose-50 border-red-200">
                    <div className="relative">
                      <div className="absolute inset-0 bg-red-400 rounded-full blur-lg opacity-30"></div>
                      <XCircle
                        className="relative h-6 w-6 text-red-600"
                        strokeWidth={2.5}
                      />
                    </div>
                    <span className="text-lg font-semibold bg-gradient-to-r from-red-600 to-rose-600 bg-clip-text text-transparent">
                      No Active Subscription
                    </span>
                  </div>

                  <p className="text-sm text-slate-600 p-4 bg-slate-50 rounded-lg border border-slate-200">
                    You don't have an active subscription. Subscribe to get full access to
                    the Sales Rep Chatbot.
                  </p>

                  <Button
                    onClick={() => navigate('/payment')}
                    className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-lg hover:shadow-xl transition-all duration-200"
                    size="lg"
                  >
                    <CreditCard className="mr-2 h-4 w-4" />
                    Subscribe Now
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {(subscriptionData?.is_subscribed ||
            (subscriptionData?.subscription_expiry_date &&
              new Date(subscriptionData.subscription_expiry_date) > new Date())) && (
            <Card className="border-2 border-slate-200 shadow-xl bg-white/90 backdrop-blur-sm">
              <CardHeader className="border-b border-slate-100 bg-gradient-to-r from-blue-50 to-indigo-50">
                <CardTitle className="text-xl text-slate-900">Billing History</CardTitle>
                <CardDescription className="text-slate-600">
                  Your subscription billing information
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                {paymentHistory.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-sm text-slate-600">
                      No payment history available.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {paymentHistory.map(invoice => (
                      <div
                        key={invoice.id}
                        className="flex items-center justify-between p-4 border-2 border-slate-200 rounded-lg bg-slate-50/50 hover:bg-slate-100/50 hover:border-blue-200 transition-all duration-200"
                      >
                        <div className="space-y-1.5">
                          <div className="flex items-center gap-2.5">
                            <span className="text-sm font-semibold text-slate-900">
                              {new Date(invoice.date).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                              })}
                            </span>
                            <span
                              className={`text-xs px-2.5 py-1 rounded-full font-medium ${
                                invoice.status === 'paid'
                                  ? 'bg-gradient-to-r from-green-100 to-emerald-100 text-green-700 border border-green-200'
                                  : invoice.status === 'open'
                                  ? 'bg-gradient-to-r from-amber-100 to-yellow-100 text-amber-700 border border-amber-200'
                                  : 'bg-gradient-to-r from-red-100 to-rose-100 text-red-700 border border-red-200'
                              }`}
                            >
                              {invoice.status.charAt(0).toUpperCase() +
                                invoice.status.slice(1)}
                            </span>
                          </div>
                          <p className="text-xs text-slate-500 font-mono">
                            Invoice #{invoice.id.substring(0, 12)}...
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-base font-bold text-slate-900">
                            ${invoice.amount.toFixed(2)} {invoice.currency.toUpperCase()}
                          </p>
                          {invoice.invoice_url && (
                            <a
                              href={invoice.invoice_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-blue-600 hover:text-blue-700 font-medium underline underline-offset-2 transition-colors inline-block mt-1"
                            >
                              View Invoice →
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
